import { showModal, withLoading } from '@/shared';
import type { ICreateMfhEstateRequest, IEstate, ILocation, IMFHBuilding, IMfhBuildingRequestParams } from '@condo/domain';
import { minBy } from 'lodash-es';
import { type MaybeRef, defineAsyncComponent, ref, unref } from 'vue';
import { useMFHBuildings, useMFHBuildingsMutations } from './mfh.hooks';

export const useMfhEstateListHooks = (mfhIdRef: MaybeRef<number>) => {
    const MFHBuildingEditor = defineAsyncComponent(() => import('@/components/mfh/one-mfh/components/MFHBuildingEditor.vue'));
    const MFHLocationEditor = defineAsyncComponent(() => import('@/components/mfh/one-mfh/components/MFHLocationEditor.vue'));
    const MoveMfhEstateModal = defineAsyncComponent(() => import('@/components/mfh/one-mfh/mfh-estate/MoveMfhEstateModal.vue'));
    const CreateMfhEstateForm = defineAsyncComponent(() => import('@/components/one-estate/CreateMfhEstateForm.vue'));

    const mfhId = unref(mfhIdRef);
    const selectedEstates = ref([]);
    const selectedBuildings = ref([]);

    const { data: mfhBuildings } = useMFHBuildings(mfhId, {
        select: ['mfhBuildingId', 'condoUuid'],
        expand: {
            location: {
                select: ['locationId', 'street', 'houseNumber', 'city', 'quarter', 'zipcode', 'country'],
            },
            estates: {
                select: ['estateId', 'condoUuid', 'mfhBuildingId', 'locationId', 'estarCoreUnitId'],
                expand: {
                    attributes: {
                        select: ['attrId', 'floor', 'floorLocation', 'locationNote', 'livingSpace', 'apartmentType', 'buildingLocationType'],
                    },
                    rentalContracts: {
                        select: ['endDate', 'updatedAt'],
                    },
                    // rentableUnitProcess: {
                    //     select: ['processId', 'processState'],
                    // },
                },
            },
        },
    });

    const { upsertLocation, upsertBuilding, deleteBuilding, moveEstatesToBuildingLocation, addEstateToBuildingMutation } = useMFHBuildingsMutations(mfhId);

    const addOrEditLocation = (mfhBuildingId?: number, editableLocation?: ILocation) => {
        const modal = showModal(MFHLocationEditor, {
            props: {
                mfhId: mfhId,
                ...(mfhBuildingId ? { mfhBuildingId } : {}),
                location: editableLocation,
            },
            events: {
                save: async ({ location, mfhBuildingId }: { location: ILocation; mfhBuildingId: number }) => {
                    await withLoading(async () => {
                        await upsertLocation({
                            mfhBuildingId,
                            location: {
                                ...location,
                                ...(editableLocation ? { locationId: editableLocation.locationId } : {}),
                            },
                        });
                        modal.close();
                    });
                },
                close: () => {
                    modal.close();
                },
            },
        });
    };

    const moveEstatesToBuilding = (estates: IEstate[], mfhBuilding?: IMFHBuilding) => {
        const mfhBuildingOptions = mfhBuilding ? [mfhBuilding] : mfhBuildings.value;

        const modal = showModal(MoveMfhEstateModal, {
            props: {
                estates,
                mfhId: mfhId,
                mfhBuildings: mfhBuildingOptions,
            },
            events: {
                save: async ({ estateIds, mfhBuildingId }: { estateIds: number[]; mfhBuildingId: number; locationId: number }) => {
                    await withLoading(async () => {
                        await moveEstatesToBuildingLocation({
                            estateIds,
                            mfhBuildingId,
                        });
                        modal.close();
                    });
                },
                close: () => {
                    modal.close();
                },
            },
        });
    };

    const addEstateToMfh = (data?: { mfhBuildingId?: number; locationId?: number }) => {
        const defaultMfhBuildingId =
            selectedBuildings.value.length === 1
                ? selectedBuildings.value[0].mfhBuildingId
                : (data?.mfhBuildingId ?? minBy(mfhBuildings.value, 'mfhBuildingId')?.mfhBuildingId);

        const modal = showModal(CreateMfhEstateForm, {
            parent: this,
            width: 1300,
            props: {
                mfhBuildings: mfhBuildings.value,
                defaultBuildingId: defaultMfhBuildingId,
                ...(data?.locationId ? { defaultLocationId: data.locationId } : {}),
            },
            events: {
                submit: async (value: ICreateMfhEstateRequest) => {
                    await withLoading(async () => {
                        await addEstateToBuildingMutation(value);
                        modal.close();
                    });
                },
            },
        });
    };

    const addBuilding = () => {
        const modal = showModal(MFHBuildingEditor, {
            props: {},
            events: {
                save: async ({ mfhBuilding, location }: IMfhBuildingRequestParams) => {
                    await withLoading(async () => {
                        await upsertBuilding({ mfhBuilding, location });
                        modal.close();
                    });
                },
                close: () => {
                    modal.close();
                },
            },
        });
    };

    const bulkMoveEstates = () => {
        moveEstatesToBuilding(selectedEstates.value);
        selectedEstates.value = [];
    };

    return {
        mfhBuildings,
        selectedEstates,
        selectedBuildings,
        addOrEditLocation,
        addBuilding,
        addEstateToMfh,
        bulkMoveEstates,
        upsertLocation,
        deleteBuilding,
        moveEstatesToBuildingLocation,
        moveEstatesToBuilding,
        addEstateToBuildingMutation,
    };
};
